import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Download,
  FileText,
  Filter,
  Plus,
  Search,
  X,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { PageLayout } from '@/components/layout/PageLayout';
import { CreateLoanModal } from '@/components/loans/CreateLoanModal';
import { StatusBadge } from '@/components/loans/StatusBadge';
import { Spinner } from '@/components/ui';
import { Button } from '@/components/ui';
import { EmptyState } from '@/components/ui/EmptyState';
import { QueryErrorState } from '@/components/ui/QueryErrorState';
import { useBulkTransitionStatus } from '@/hooks/use-bulk-actions';
import { type LoanListParams, useInfiniteLoanList } from '@/hooks/use-loans';
import { exportToCSV } from '@/lib/csv';
import { handleMutationError } from '@/lib/error-handling';
import { fmtCurrency, fmtDate } from '@/lib/format';
import { hasRoleAccess } from '@/lib/role-access';
import { useAuthStore } from '@/stores/auth-store';
import { toast } from '@/stores/toast-store';
import { LOAN_STATUS_CONFIG, type LoanStatus, type LoanType } from '@/types';

const LOAN_TYPE_LABELS: Record<string, string> = {
  PERSONAL: 'Personal',
  BUSINESS: 'Business',
  MORTGAGE: 'Mortgage',
  AUTO: 'Auto',
  LINE_OF_CREDIT: 'LOC',
};

const ALL_STATUSES = Object.keys(LOAN_STATUS_CONFIG) as LoanStatus[];

const LOAN_TYPES: LoanType[] = ['PERSONAL', 'BUSINESS', 'MORTGAGE', 'AUTO', 'LINE_OF_CREDIT'];

type SortKey =
  'loanNumber' | 'borrower' | 'principal' | 'interestRate' | 'termMonths' | 'createdAt';
type SortDir = 'asc' | 'desc';

function SortTh({
  label,
  sortKey: key,
  currentKey,
  dir,
  onSort,
}: {
  label: string;
  sortKey: SortKey;
  currentKey: SortKey;
  dir: SortDir;
  onSort: (key: SortKey) => void;
}) {
  const active = currentKey === key;
  return (
    <th
      onClick={() => onSort(key)}
      className="text-left text-xs font-medium text-gray-500 px-4 py-3 cursor-pointer hover:text-gray-700 select-none"
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {active ? (
          dir === 'asc' ? (
            <ArrowUp className="w-3 h-3" />
          ) : (
            <ArrowDown className="w-3 h-3" />
          )
        ) : (
          <ArrowUpDown className="w-3 h-3 opacity-30" />
        )}
      </span>
    </th>
  );
}

export function LoanList() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Read initial status from URL params (e.g. ?status=APPLICATION)
  const urlStatus = searchParams.get('status') as LoanStatus | null;
  const [statusFilter, setStatusFilter] = useState<LoanStatus | ''>(urlStatus ?? '');
  const [typeFilter, setTypeFilter] = useState<LoanType | ''>('');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [showFilters, setShowFilters] = useState(!!urlStatus);
  const [showCreate, setShowCreate] = useState(false);
  const [sortKey, setSortKey] = useState<SortKey>('createdAt');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const user = useAuthStore(s => s.user);
  const bulkTransition = useBulkTransitionStatus();
  const canBulkAction = hasRoleAccess(user?.role, ['ADMIN', 'MANAGER']);

  // Debounce search input — wait 300ms after user stops typing
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery.trim());
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Listen for global "new loan" keyboard shortcut event
  useEffect(() => {
    const handler = () => setShowCreate(true);
    window.addEventListener('app:new-loan', handler);
    return () => window.removeEventListener('app:new-loan', handler);
  }, []);

  // Sync status filter to URL
  useEffect(() => {
    const newParams = new URLSearchParams(searchParams);
    if (statusFilter) {
      newParams.set('status', statusFilter);
    } else {
      newParams.delete('status');
    }
    setSearchParams(newParams, { replace: true });
  }, [statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const activeFilterCount = [statusFilter, typeFilter].filter(Boolean).length;

  // Map frontend sort keys to backend field names
  const sortFieldMap: Record<SortKey, string> = {
    loanNumber: 'loanNumber',
    borrower: 'borrower',
    principal: 'principal',
    interestRate: 'interestRate',
    termMonths: 'termMonths',
    createdAt: 'createdAt',
  };

  const params: LoanListParams = {
    limit: 20,
    ...(statusFilter ? { status: statusFilter } : {}),
    ...(debouncedSearch ? { search: debouncedSearch } : {}),
    ...(typeFilter ? { loanType: typeFilter } : {}),
    sortBy: sortFieldMap[sortKey],
    sortDir,
  };

  const {
    data,
    isLoading,
    isError,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteLoanList(params);

  useEffect(() => {
    const scrollRoot = document.getElementById('scrollable');
    const pageScrollRoot = scrollRoot?.parentElement?.parentElement;
    if (!hasNextPage || isFetchingNextPage) return;

    const loadMoreWhenNearBottom = () => {
      if (!pageScrollRoot) return;

      const remaining =
        pageScrollRoot.scrollHeight - pageScrollRoot.scrollTop - pageScrollRoot.clientHeight;
      if (remaining < 240) void fetchNextPage();
    };

    pageScrollRoot?.addEventListener('scroll', loadMoreWhenNearBottom, { passive: true });
    loadMoreWhenNearBottom();
    return () => {
      pageScrollRoot?.removeEventListener('scroll', loadMoreWhenNearBottom);
    };
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  function toggleSort(key: SortKey) {
    if (sortKey === key) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir(key === 'createdAt' ? 'desc' : 'asc');
    }
  }

  function toggleSelect(id: string) {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleSelectAll() {
    if (filteredLoans.length === 0) return;
    if (filteredLoans.every(loan => selectedIds.has(loan.id))) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredLoans.map(loan => loan.id)));
    }
  }

  function handleBulkStatus(status: LoanStatus) {
    const ids = Array.from(selectedIds);
    bulkTransition.mutate(
      { loanIds: ids, status, reason: `Bulk action by ${user?.firstName}` },
      {
        onSuccess: result => {
          toast.success(`${result.succeeded} of ${result.total} loans updated`);
          setSelectedIds(new Set());
        },
        onError: (err: any) => handleMutationError(err, 'Bulk action failed'),
      },
    );
  }

  // Server handles sorting, filtering, and pagination — just use data directly
  const filteredLoans = data?.pages.flatMap(page => page.loans) ?? [];
  const totalLoans = data?.pages[0]?.total ?? 0;

  return (
    <PageLayout
      title="Loans"
      stretchContent={false}
      headerAction={
        <div className="flex w-full items-center justify-end gap-2 sm:w-auto">
          {data && filteredLoans.length > 0 && (
            <button
              onClick={() => {
                const headers = [
                  'Loan #',
                  'Borrower',
                  'Type',
                  'Principal',
                  'Rate',
                  'Term',
                  'Status',
                  'Created',
                ];
                const rows = filteredLoans.map(l => ({
                  'Loan #': l.loanNumber,
                  Borrower: l.borrower?.name ?? '',
                  Type: LOAN_TYPE_LABELS[l.loanType] ?? l.loanType,
                  Principal: l.principal,
                  Rate: `${parseFloat(l.interestRate).toFixed(2)}%`,
                  Term: `${l.termMonths} months`,
                  Status: LOAN_STATUS_CONFIG[l.status]?.label ?? l.status,
                  Created: fmtDate(l.createdAt),
                }));
                exportToCSV(
                  headers,
                  rows,
                  `loans-export-${new Date().toISOString().split('T')[0]}`,
                );
              }}
              className="inline-flex min-h-[36px] min-w-[120px] shrink-0 items-center justify-center gap-1.5 rounded-md border border-gray-200 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
            >
              <Download className="w-3.5 h-3.5" />
              Export
            </button>
          )}
          <Button
            variant="primary"
            onClick={() => setShowCreate(true)}
            className="!w-auto min-h-[36px] min-w-[120px] shrink-0 gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors hover:!bg-gray-700"
          >
            <Plus className="w-3.5 h-3.5 min-w-3.5 min-h-3.5" />
            New Loan
          </Button>
          <Button
            variant="secondary"
            onClick={() => setShowFilters(!showFilters)}
            className={`!w-auto min-h-[36px] min-w-[120px] shrink-0 gap-2 rounded-md border px-3 py-1.5 text-sm transition-colors ${
              showFilters || activeFilterCount > 0
                ? 'border-blue-500 bg-indigo-50 text-blue-700'
                : 'border-gray-200 text-gray-600 hover:bg-gray-50'
            }`}
          >
            <Filter className="w-3.5 h-3.5 min-w-3.5 min-h-3.5" />
            Filters
            {activeFilterCount > 0 && (
              <span className="bg-indigo-600 text-white text-[10px] px-1.5 py-0.5 rounded-full">
                {activeFilterCount}
              </span>
            )}
          </Button>
        </div>
      }
      headerBottomContent={
        data ? (
          <span className="text-xs font-medium text-gray-500">{totalLoans} total loans</span>
        ) : null
      }
      contentClassName="space-y-4"
    >
      <CreateLoanModal
        open={showCreate}
        onClose={() => setShowCreate(false)}
        onCreated={loan => navigate(`/loans/${loan.id}`)}
      />

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="text"
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          placeholder="Search by loan number or borrower name..."
          className="w-full pl-9 pr-3 py-2.5 bg-white rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      {/* Filters panel */}
      {showFilters && (
        <div className="bg-white rounded-lg border border-gray-200 p-4 flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600">Status:</label>
            <select
              value={statusFilter}
              onChange={e => {
                setStatusFilter(e.target.value as LoanStatus | '');
              }}
              className="rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
            >
              <option value="">All statuses</option>
              {ALL_STATUSES.map(s => (
                <option key={s} value={s}>
                  {LOAN_STATUS_CONFIG[s].label}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600">Type:</label>
            <select
              value={typeFilter}
              onChange={e => {
                setTypeFilter(e.target.value as LoanType | '');
              }}
              className="rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
            >
              <option value="">All types</option>
              {LOAN_TYPES.map(t => (
                <option key={t} value={t}>
                  {LOAN_TYPE_LABELS[t] ?? t}
                </option>
              ))}
            </select>
          </div>

          {activeFilterCount > 0 && (
            <button
              onClick={() => {
                setStatusFilter('');
                setTypeFilter('');
              }}
              className="flex items-center gap-1 text-xs text-indigo-600 hover:underline"
            >
              <X className="w-3 h-3" />
              Clear all
            </button>
          )}
        </div>
      )}

      {/* Bulk action bar */}
      {canBulkAction && selectedIds.size > 0 && (
        <div className="bg-indigo-50 border border-indigo-200 rounded-lg px-4 py-2.5 flex items-center gap-3">
          <span className="text-sm font-medium text-indigo-700">{selectedIds.size} selected</span>
          <div className="flex items-center gap-2 ml-auto">
            <button
              onClick={() => handleBulkStatus('UNDER_REVIEW')}
              disabled={bulkTransition.isPending}
              className="px-3 py-1 text-xs font-medium bg-amber-100 text-amber-700 rounded-md hover:bg-amber-200 transition-colors"
            >
              Move to Review
            </button>
            <button
              onClick={() => handleBulkStatus('APPROVED')}
              disabled={bulkTransition.isPending}
              className="px-3 py-1 text-xs font-medium bg-green-100 text-green-700 rounded-md hover:bg-green-200 transition-colors"
            >
              Approve
            </button>
            <button
              onClick={() => handleBulkStatus('CANCELLED')}
              disabled={bulkTransition.isPending}
              className="px-3 py-1 text-xs font-medium bg-red-100 text-red-700 rounded-md hover:bg-red-200 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={() => setSelectedIds(new Set())}
              className="px-3 py-1 text-xs text-gray-500 hover:text-gray-700"
            >
              Clear
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-20 text-gray-400">
            <Spinner size="lg" className="w-5 h-5 animate-spin mr-2" />
            Loading loans…
          </div>
        ) : isError ? (
          <QueryErrorState error={error} subject="loans" onRetry={() => refetch()} />
        ) : !data || filteredLoans.length === 0 ? (
          <EmptyState
            icon={FileText}
            title={
              searchQuery || statusFilter || typeFilter
                ? 'No loans match your filters'
                : 'No loans yet'
            }
            description={
              searchQuery || statusFilter || typeFilter
                ? 'Try adjusting your search or filters'
                : 'Create your first loan to get started'
            }
            action={
              !(searchQuery || statusFilter || typeFilter)
                ? { label: 'Create Loan', onClick: () => setShowCreate(true) }
                : undefined
            }
          />
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50">
                {canBulkAction && (
                  <th className="px-3 py-3 w-8">
                    <input
                      type="checkbox"
                      checked={
                        filteredLoans.length > 0 &&
                        filteredLoans.every(loan => selectedIds.has(loan.id))
                      }
                      onChange={toggleSelectAll}
                      className="rounded border-gray-300 text-indigo-600 focus:ring-blue-500"
                    />
                  </th>
                )}
                <SortTh
                  label="Loan #"
                  sortKey="loanNumber"
                  currentKey={sortKey}
                  dir={sortDir}
                  onSort={toggleSort}
                />
                <SortTh
                  label="Borrower"
                  sortKey="borrower"
                  currentKey={sortKey}
                  dir={sortDir}
                  onSort={toggleSort}
                />
                <th className="text-left text-xs font-medium text-gray-500 px-4 py-3">Type</th>
                <SortTh
                  label="Principal"
                  sortKey="principal"
                  currentKey={sortKey}
                  dir={sortDir}
                  onSort={toggleSort}
                />
                <SortTh
                  label="Rate"
                  sortKey="interestRate"
                  currentKey={sortKey}
                  dir={sortDir}
                  onSort={toggleSort}
                />
                <SortTh
                  label="Term"
                  sortKey="termMonths"
                  currentKey={sortKey}
                  dir={sortDir}
                  onSort={toggleSort}
                />
                <th className="text-left text-xs font-medium text-gray-500 px-4 py-3">Status</th>
                <SortTh
                  label="Created"
                  sortKey="createdAt"
                  currentKey={sortKey}
                  dir={sortDir}
                  onSort={toggleSort}
                />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredLoans.map(loan => (
                <tr
                  key={loan.id}
                  onClick={() => navigate(`/loans/${loan.id}`)}
                  className={`hover:bg-gray-50 cursor-pointer transition-colors ${selectedIds.has(loan.id) ? 'bg-indigo-50' : ''}`}
                >
                  {canBulkAction && (
                    <td className="px-3 py-3" onClick={e => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selectedIds.has(loan.id)}
                        onChange={() => toggleSelect(loan.id)}
                        className="rounded border-gray-300 text-indigo-600 focus:ring-blue-500"
                      />
                    </td>
                  )}
                  <td className="px-4 py-3 text-sm font-mono font-medium text-indigo-600">
                    {loan.loanNumber}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900">{loan.borrower?.name ?? '—'}</td>
                  <td className="px-4 py-3 text-sm text-gray-500">
                    {LOAN_TYPE_LABELS[loan.loanType] ?? loan.loanType}
                  </td>
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">
                    {fmtCurrency(loan.principal)}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">
                    {parseFloat(loan.interestRate).toFixed(2)}%
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">{loan.termMonths}mo</td>
                  <td className="px-4 py-3">
                    <StatusBadge status={loan.status} />
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-400">{fmtDate(loan.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        <div className="flex min-h-12 items-center justify-center border-t border-gray-200">
          {isFetchingNextPage && <Spinner size="sm" className="h-5 w-5 text-indigo-600" />}
        </div>
      </div>
    </PageLayout>
  );
}
